import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Link, useSearchParams } from 'react-router-dom';
import './OrderSuccess.css';
import axios from 'axios';
import { RiArrowRightUpLine } from 'react-icons/ri';
import { usePublicSettings } from '../../context/PublicSettingsContext';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { formatOrderMoney } from '../../utils/currencyFormat';
import LanguageContext from '../../context/LanguageContext.js';
import { getVariantSizeDisplay } from '../../utils/variantDisplay';
import { retryOnlinePayment } from '../../api/orderApi';
import { useOnlinePaymentStatus } from '../../hooks/useOnlinePaymentStatus';
import { rememberPendingOnlinePayment, takePendingOnlinePayment } from '../../utils/pendingOnlinePayment';
import { useStorePath } from '../../lib/storefront/storePath.js';
import KineticLoader from '../../components/UI/KineticLoader/KineticLoader';

// Which payment message an unpaid online order shows, by polling phase.
const PAYMENT_VIEW_BY_PHASE = {
    idle: 'confirming',
    checking: 'confirming',
    settled: 'confirming',
    awaiting: 'notCompleted',
    slow: 'slow',
    closed: 'failed',
};

const OrderSuccess = () => {
    const { dir } = useContext(LanguageContext);
    const { t } = useTranslation();
    const currentLanguage = i18n.language;
    const { currencyCode, storeLocale } = usePublicSettings();
    const { clearCart } = useCart();
    const { addToast } = useToast();
    const storePath = useStorePath();
    const moneyLocaleOpts = { locale: storeLocale };
    const showTrackOrdersLinks = (process.env.REACT_APP_SHOW_TRACK_ORDERS_LINKS || 'false').toLowerCase() === 'true';
    const [searchParams] = useSearchParams();
    const orderNumber = searchParams.get('orderNumber');
    // Set by the payment provider when the customer left its page without paying.
    const leftPaymentPage = searchParams.get('payment') === 'cancelled';
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState(false);
    const layoutDir = dir();

    const fetchOrderDetails = useCallback(async () => {
        try {
            const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:9090';
            const response = await axios.get(`${API_URL}/api/food-order/public/track/${orderNumber}`);

            if (response.data.status === 'success') {
                setOrderDetails(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
        } finally {
            setLoading(false);
        }
    }, [orderNumber]);

    useEffect(() => {
        if (orderNumber) {
            fetchOrderDetails();
        } else {
            setLoading(false);
        }
    }, [orderNumber, fetchOrderDetails]);

    const isOnlineOrder = orderDetails?.payment?.paymentMethod === 'online';
    const awaitingPayment = isOnlineOrder && orderDetails?.orderStatus === 'awaiting-payment';
    const { phase, checkAgain } = useOnlinePaymentStatus(orderNumber, {
        enabled: awaitingPayment,
        leftPaymentPage,
    });

    // The payment was confirmed or the order closed: show the order as it now stands.
    useEffect(() => {
        if (phase === 'settled' || phase === 'closed') {
            fetchOrderDetails();
        }
    }, [phase, fetchOrderDetails]);

    // The cart was kept in case the payment failed; once paid it can go.
    const isPaid = orderDetails?.payment?.paymentStatus === 'paid';
    useEffect(() => {
        if (isOnlineOrder && isPaid && takePendingOnlinePayment(orderNumber)) {
            clearCart();
        }
    }, [isOnlineOrder, isPaid, orderNumber, clearCart]);

    let paymentView = null;
    if (isOnlineOrder && !isPaid) {
        if (orderDetails.orderStatus === 'cancelled') {
            paymentView = 'failed';
        } else if (awaitingPayment) {
            paymentView = PAYMENT_VIEW_BY_PHASE[phase] || 'confirming';
        }
    }

    const handleRetryPayment = async () => {
        setRetrying(true);
        try {
            const response = await retryOnlinePayment(orderNumber, storePath(`/${currentLanguage}/order-success`));
            const redirectUrl = response?.checkout?.redirectUrl;
            if (!redirectUrl) throw new Error('Missing payment page');
            rememberPendingOnlinePayment(orderNumber);
            window.location.assign(redirectUrl);
        } catch (error) {
            addToast(error?.response?.data?.message || t('orderSuccess.payment.retryFailed'), 'error');
            setRetrying(false);
        }
    };

    const getStatusText = (status) => {
        switch (status?.toLowerCase()) {
            case 'awaiting-payment':
                return t('myOrders.statusAwaitingPayment');
            case 'pending':
                return t('myOrders.statusPending');
            case 'confirmed':
                return t('myOrders.statusConfirmed');
            case 'preparing':
                return t('myOrders.statusPreparing');
            case 'ready':
                return t('myOrders.statusReady');
            case 'delivered':
                return t('myOrders.statusDelivered');
            case 'completed':
                return t('myOrders.statusCompleted');
            case 'cancelled':
                return t('myOrders.statusCancelled');
            default:
                return status;
        }
    };

    const orderTypeLabel = (type) => {
        const key = type?.toLowerCase?.() || type;
        const map = {
            delivery: t('checkout.delivery'),
            pickup: t('checkout.pickup'),
            'dine-in': t('checkout.dineIn'),
        };
        return map[key] || type;
    };

    return (
        <main className="main">
            <section className="order-success-page">
                <div className="container order-success-header" dir={layoutDir}>
                    <span className="order-success-eyebrow">
                        {paymentView ? t('orderSuccess.payment.eyebrow') : t('orderSuccess.eyebrow')}
                    </span>
                    <h1 className="order-success-page-title">
                        {paymentView ? t(`orderSuccess.payment.${paymentView}Title`) : t('orderSuccess.title')}
                    </h1>
                    <p className="order-success-lead">
                        {paymentView ? t(`orderSuccess.payment.${paymentView}Text`) : t('orderSuccess.message')}
                    </p>
                </div>

                <div className="container order-success-inner" dir={layoutDir}>
                    <div className="order-success-card">
                        {paymentView === 'confirming' ? (
                            <div className="order-success-loading" role="status">
                                <KineticLoader />
                            </div>
                        ) : !paymentView ? (
                            <div className="order-success-visual" aria-hidden="true">
                                <div className="order-success-ring">
                                    <svg viewBox="0 0 60 60" fill="none">
                                        <circle
                                            cx="30"
                                            cy="30"
                                            r="28"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeDasharray="176"
                                            strokeDashoffset="0"
                                            className="order-success-circle"
                                        />
                                        <path
                                            d="M18 30l9 9 15-15"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="order-success-check"
                                        />
                                    </svg>
                                </div>
                            </div>
                        ) : null}

                        {paymentView && paymentView !== 'confirming' ? (
                            <div className="order-success-actions">
                                {paymentView === 'notCompleted' && (
                                    <button
                                        type="button"
                                        className="order-success-btn order-success-btn--primary"
                                        onClick={handleRetryPayment}
                                        disabled={retrying}
                                    >
                                        <span>{t('orderSuccess.payment.tryAgain')}</span>
                                    </button>
                                )}
                                {paymentView === 'slow' && (
                                    <button
                                        type="button"
                                        className="order-success-btn order-success-btn--primary"
                                        onClick={checkAgain}
                                    >
                                        <span>{t('orderSuccess.payment.checkAgain')}</span>
                                    </button>
                                )}
                                {(paymentView === 'notCompleted' || paymentView === 'failed') && (
                                    <Link to={`/${currentLanguage}/cart`} className="order-success-btn order-success-btn--outline">
                                        {t('orderSuccess.payment.backToCart')}
                                    </Link>
                                )}
                            </div>
                        ) : null}

                        {loading ? (
                            <div className="order-success-loading" role="status">
                                {t('orderSuccess.loading')}
                            </div>
                        ) : orderDetails ? (
                            <div className="order-success-details">
                                <div className="order-success-dl">
                                    <div className="order-success-row">
                                        <span className="order-success-label">{t('orderSuccess.orderNumber')}</span>
                                        <span className="order-success-value order-success-value--accent">
                                            {orderDetails.orderNumber}
                                        </span>
                                    </div>

                                    <div className="order-success-row">
                                        <span className="order-success-label">{t('orderSuccess.orderType')}</span>
                                        <span className="order-success-value">
                                            {orderTypeLabel(orderDetails.customerInfo?.orderType)}
                                        </span>
                                    </div>

                                    <div className="order-success-row">
                                        <span className="order-success-label">{t('orderSuccess.total')}</span>
                                        <span className="order-success-value order-success-value--total">
                                            {formatOrderMoney(
                                                orderDetails.totalAmount,
                                                orderDetails.currency,
                                                currencyCode,
                                                moneyLocaleOpts
                                            )}
                                        </span>
                                    </div>

                                    <div className="order-success-row">
                                        <span className="order-success-label">{t('orderSuccess.status')}</span>
                                        <span className="order-success-pill">{getStatusText(orderDetails.orderStatus)}</span>
                                    </div>

                                    {orderDetails.estimatedCompletionTime && (
                                        <div className="order-success-row">
                                            <span className="order-success-label">{t('orderSuccess.estimatedTime')}</span>
                                            <span className="order-success-value">
                                                {new Date(orderDetails.estimatedCompletionTime).toLocaleTimeString(
                                                    currentLanguage
                                                )}
                                            </span>
                                        </div>
                                    )}

                                    {orderDetails.customerInfo?.orderType === 'delivery' &&
                                        orderDetails.customerInfo?.deliveryAddress && (
                                            <div className="order-success-row order-success-row--stack">
                                                <span className="order-success-label">{t('orderSuccess.deliveryAddress')}</span>
                                                <span className="order-success-value order-success-value--multiline">
                                                    {orderDetails.customerInfo.deliveryAddress.street},
                                                    {orderDetails.customerInfo.deliveryAddress.apartment &&
                                                        ` ${orderDetails.customerInfo.deliveryAddress.apartment},`}
                                                    <br />
                                                    {orderDetails.customerInfo.deliveryAddress.city},
                                                    {orderDetails.customerInfo.deliveryAddress.postalCode}
                                                </span>
                                            </div>
                                        )}
                                </div>

                                <div className="order-success-items">
                                    <h2 className="order-success-items-title">{t('orderSuccess.orderItems')}</h2>
                                    <ul className="order-success-item-list">
                                        {orderDetails.items?.map((item, index) => {
                                            const sizeLabel = getVariantSizeDisplay(item.variant?.size, currentLanguage);
                                            return (
                                            <li key={index} className="order-success-item">
                                                <div className="order-success-item-main">
                                                    {item.primaryImage && (
                                                        <img
                                                            src={item.primaryImage}
                                                            alt={item.name?.[currentLanguage] || item.name?.en || ''}
                                                            className="order-success-item-img"
                                                        />
                                                    )}
                                                    <div className="order-success-item-text">
                                                        <span className="order-success-item-name">
                                                            {item.name?.[currentLanguage] || item.name?.en}
                                                            {sizeLabel ? (
                                                                <span className="order-success-item-name-size"> ({sizeLabel})</span>
                                                            ) : null}
                                                        </span>
                                                    </div>
                                                </div>
                                                <span className="order-success-item-qty">×{item.quantity}</span>
                                                <span className="order-success-item-price">
                                                    {formatOrderMoney(item.price, orderDetails.currency, currencyCode, moneyLocaleOpts)}
                                                </span>
                                            </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        ) : orderNumber ? (
                            <div className="order-success-fallback">
                                <p className="order-success-fallback-label">{t('orderSuccess.orderNumberLabel')}</p>
                                <p className="order-success-fallback-number">{orderNumber}</p>
                                <p className="order-success-fallback-note">{t('orderSuccess.trackNote')}</p>
                            </div>
                        ) : null}

                        <div className="order-success-actions">
                            <Link to={`/${currentLanguage}/menu`} className="order-success-btn order-success-btn--primary">
                                <span>{t('orderSuccess.continueShopping')}</span>
                                <RiArrowRightUpLine className="order-success-btn-ico" aria-hidden />
                                <span className="order-success-btn-shine" aria-hidden />
                            </Link>
                            {showTrackOrdersLinks && orderNumber && (
                                <Link
                                    to={`/${currentLanguage}/track-order?orderNumber=${orderNumber}`}
                                    className="order-success-btn order-success-btn--outline"
                                >
                                    {t('orderSuccess.trackOrder')}
                                </Link>
                            )}
                        </div>

                        <div className="order-success-foot">
                            <p>{t('orderSuccess.emailConfirmation')}</p>
                            <p>{t('orderSuccess.contactInfo')}</p>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default OrderSuccess;
